import { Button, Icon } from '@iwoca/orion';
import TopUpStatusMessage from './TopUpStatusMessage';
import styles from '../RequestMoreFundsPanel.module.css';
import { useQuery } from '@tanstack/react-query';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { FormattedDate } from 'react-intl';
import { useState } from 'react';
import DrawdownPanel from '../../DrawdownPanel/DrawdownPanel';
import { TopUpForm } from '../../TopUpForm/TopUpForm';
import { useGetApplication } from '@api/lendingAPI/lapiHooks';

type TOptions = 'none_selected' | 'drawdown' | 'request_more_funds';

export const PreApproved = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useQuery(getAccountsQueryConfig(stateKey));
    const [selectedOption, setSelectedOption] =
        useState<TOptions>('none_selected');

    const {
        data: applicationResponse,
        isLoading: applicationLoading,
        isError: applicationError
    } = useGetApplication(stateKey);

    if (
        accountsLoading ||
        applicationLoading ||
        accountsError ||
        applicationError
    )
        return null;

    const account = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const approvalAmount = account.latest_formal_offer?.available_cash;
    const expiryDate = account.latest_formal_offer?.timeout;

    const applicationDeclined = applicationResponse!.status === 'declined';

    switch (selectedOption) {
        case 'drawdown':
            return (
                <>
                    <BackButton setSelectedOption={setSelectedOption} />
                    <DrawdownPanel />
                </>
            );
        case 'request_more_funds':
            return (
                <>
                    {!applicationDeclined && (
                        <BackButton setSelectedOption={setSelectedOption} />
                    )}
                    <TopUpForm
                        accountId={accountId}
                        formHeader={
                            <div className={styles.formHeader}>
                                <h2 className={styles.heading}>
                                    Request more funds
                                </h2>
                                <p className={styles.description}>
                                    You're already approved for{' '}
                                    <strong>
                                        <FormattedCurrency
                                            value={approvalAmount!}
                                        />
                                    </strong>
                                    . Approval to more funds is subject to
                                    credit review.
                                </p>
                            </div>
                        }
                    />
                </>
            );
        default:
            return (
                <>
                    <TopUpStatusMessage
                        heading="You've been automatically approved"
                        description={
                            <span>
                                You can drawdown up to{' '}
                                <strong>
                                    <FormattedCurrency
                                        value={approvalAmount!}
                                    />
                                </strong>{' '}
                                before{' '}
                                <strong>
                                    <FormattedDate
                                        value={new Date(expiryDate!)}
                                        day="2-digit"
                                        month="short"
                                        year="numeric"
                                    />
                                </strong>
                            </span>
                        }
                    />
                    <div className={styles.extraContentContainer}>
                        <Button
                            variant="success"
                            className={styles.cta}
                            onClick={() => setSelectedOption('drawdown')}
                        >
                            Choose how much
                        </Button>
                        {!applicationDeclined && (
                            <div className={styles.extraContent}>
                                <p className={styles.extraContentDescription}>
                                    Need more than{' '}
                                    <strong>
                                        <FormattedCurrency
                                            value={approvalAmount!}
                                        />
                                        ?
                                    </strong>
                                </p>
                                <Button
                                    variant="tertiary"
                                    onClick={() =>
                                        setSelectedOption('request_more_funds')
                                    }
                                >
                                    Request more funds
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            );
    }
};

const BackButton = ({
    setSelectedOption
}: {
    setSelectedOption: React.Dispatch<React.SetStateAction<TOptions>>;
}) => {
    return (
        <Button
            variant="tertiary"
            onClick={() => setSelectedOption('none_selected')}
            className={styles.backButton}
        >
            <Icon icon="arrowLeft" />
            Back
        </Button>
    );
};
